import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import type { Access, AccessKind, AccessRow, AccessRowType } from '../types'
import { ACCESS_KIND_LABEL } from '../types'
import { useEscClose } from '../lib/useEscClose'

interface Props {
  clientId: number
  /** null — создаём новую запись доступа выбранного типа. */
  access: Access | null
  kind: AccessKind
  onClose: () => void
}

interface RowDraft extends AccessRow {
  key: string
}

let rowSeq = 0
const newKey = () => `r${++rowSeq}`

function toDrafts(rows: AccessRow[]): RowDraft[] {
  return rows.map((r) => ({ ...r, key: newKey() }))
}

function emptyRow(row_type: AccessRowType): RowDraft {
  return { key: newKey(), row_type, url: '', product_id: null, computer: '', user_name: '', login: '', password: '' }
}

/** Редактор реквизитов доступа. Набор полей зависит от типа сервиса. */
export default function AccessEditor({ clientId, access, kind, onClose }: Props) {
  const qc = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [error, setError] = useState('')

  const [title, setTitle] = useState(access?.title ?? ACCESS_KIND_LABEL[kind])
  const [login, setLogin] = useState(access?.login ?? '')
  const [password, setPassword] = useState(access?.password ?? '')
  const [serverAddress, setServerAddress] = useState(access?.server_address ?? '')
  const [productId, setProductId] = useState(access?.product_id ? String(access.product_id) : '')
  const [serviceId, setServiceId] = useState(access?.service_id ? String(access.service_id) : '')
  const [recoveryPhone, setRecoveryPhone] = useState(access?.recovery_phone ?? '')
  const [recoveryEmail, setRecoveryEmail] = useState(access?.recovery_email ?? '')
  const [note, setNote] = useState(access?.note ?? '')
  const [rows, setRows] = useState<RowDraft[]>(() => toDrafts(access?.rows ?? []))
  // Запись, к которой цепляются файлы. Для новой появляется после автосохранения.
  const [current, setCurrent] = useState<Access | null>(access)

  const products = useQuery({ queryKey: ['products'], queryFn: () => api.products() })
  const services = useQuery({ queryKey: ['portal-services'], queryFn: () => api.portalServices() })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['accesses', clientId] })
    qc.invalidateQueries({ queryKey: ['clients'] })
  }

  /** Тело запроса собирается и для обычного сохранения, и для автосохранения перед загрузкой файла. */
  function buildPayload(): Record<string, unknown> {
    return {
      title: title.trim(),
      login: login || null,
      password: password || null,
      server_address: serverAddress || null,
      product_id: productId ? Number(productId) : null,
      service_id: serviceId ? Number(serviceId) : null,
      recovery_phone: recoveryPhone || null,
      recovery_email: recoveryEmail || null,
      note: note || null,
      rows: rows.map(({ key, id, position, product_name, ...r }) => ({
        ...r,
        product_id: r.product_id || null,
      })),
    }
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = buildPayload()
      return current
        ? api.updateAccess(current.id, payload)
        : api.createAccess(clientId, { ...payload, kind })
    },
    onSuccess: () => {
      refresh()
      onClose()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось сохранить доступ'),
  })

  /** Прикрепление файла сначала сохраняет форму: файл цепляется к записи,
   *  а форма при этом остаётся открытой — введённое не теряется. */
  const attach = useMutation({
    mutationFn: async (file: File) => {
      const payload = buildPayload()
      const saved = current
        ? await api.updateAccess(current.id, payload)
        : await api.createAccess(clientId, { ...payload, kind })
      return api.uploadAccessFile(saved.id, file)
    },
    onSuccess: (updated) => {
      setCurrent(updated)
      // Из RDP-файла сервер вытащил адрес сервера — подхватываем его в форму
      if (updated.server_address) setServerAddress(updated.server_address)
      refresh()
      qc.invalidateQueries({ queryKey: ['access', updated.id] })
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось загрузить файл'),
  })

  const removeFile = useMutation({
    mutationFn: (id: number) => api.deleteAccessFile(id),
    onSuccess: async () => {
      if (current) setCurrent(await api.access(current.id))
      refresh()
    },
  })

  function patchRow(key: string, patch: Partial<AccessRow>) {
    setRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  function addRow(type: AccessRowType) {
    setRows([...rows, emptyRow(type)])
  }

  const of = (type: AccessRowType) => rows.filter((r) => r.row_type === type)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!title.trim()) return setError('Укажите название записи')
    save.mutate()
  }

  useEscClose(onClose)

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal wide" onSubmit={submit}>
        <h2>
          {current ? 'Доступ' : 'Новый доступ'} · {ACCESS_KIND_LABEL[kind]}
        </h2>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}

        <div className="grid-2">
          <div className="field full">
            <label>Название записи</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>

          {kind === 'cloud' && (
            <>
              <div className="field">
                <label>Логин</label>
                <input value={login} onChange={(e) => setLogin(e.target.value)} />
              </div>
              <div className="field">
                <label>Пароль</label>
                <input value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div className="field full">
                <label>Адрес сервера</label>
                <input
                  value={serverAddress}
                  onChange={(e) => setServerAddress(e.target.value)}
                  placeholder="подставится из RDP-файла"
                />
              </div>
            </>
          )}

          {kind === 'database' && (
            <div className="field full">
              <label>Продукт</label>
              <select value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">—</option>
                {products.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {kind === 'portal' && (
            <>
              <div className="field full">
                <label>Название сервиса</label>
                <select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
                  <option value="">—</option>
                  {services.data?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Телефон для восстановления</label>
                <input value={recoveryPhone} onChange={(e) => setRecoveryPhone(e.target.value)} />
              </div>
              <div className="field">
                <label>Почта для восстановления</label>
                <input value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} />
              </div>
            </>
          )}

          <div className="field full">
            <label>Примечание</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        {kind === 'cloud' && (
          <>
            <RowBlock
              title="Базы"
              rows={of('base_link')}
              onAdd={() => addRow('base_link')}
              onRemove={(key) => setRows(rows.filter((r) => r.key !== key))}
              columns={['Ссылка на базу', 'Продукт']}
              render={(row) => (
                <>
                  <td>
                    <input
                      value={row.url ?? ''}
                      onChange={(e) => patchRow(row.key, { url: e.target.value })}
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td>
                    <select
                      value={row.product_id ? String(row.product_id) : ''}
                      onChange={(e) =>
                        patchRow(row.key, { product_id: e.target.value ? Number(e.target.value) : null })
                      }
                      style={{ width: '100%' }}
                    >
                      <option value="">—</option>
                      {products.data?.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </>
              )}
            />

            <RowBlock
              title="Компьютеры"
              rows={of('computer')}
              onAdd={() => addRow('computer')}
              onRemove={(key) => setRows(rows.filter((r) => r.key !== key))}
              columns={['Компьютер', 'Пользователь']}
              render={(row) => (
                <>
                  <td>
                    <input
                      value={row.computer ?? ''}
                      onChange={(e) => patchRow(row.key, { computer: e.target.value })}
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td>
                    <input
                      value={row.user_name ?? ''}
                      onChange={(e) => patchRow(row.key, { user_name: e.target.value })}
                      style={{ width: '100%' }}
                    />
                  </td>
                </>
              )}
            />
          </>
        )}

        {kind === 'database' && (
          <RowBlock
            title="Пользователи базы"
            rows={of('credential')}
            onAdd={() => addRow('credential')}
            onRemove={(key) => setRows(rows.filter((r) => r.key !== key))}
            columns={['Пользователь', 'Логин', 'Пароль']}
            render={(row) => (
              <>
                <td>
                  <input
                    value={row.user_name ?? ''}
                    onChange={(e) => patchRow(row.key, { user_name: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    value={row.login ?? ''}
                    onChange={(e) => patchRow(row.key, { login: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    value={row.password ?? ''}
                    onChange={(e) => patchRow(row.key, { password: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </td>
              </>
            )}
          />
        )}

        {kind === 'portal' && (
          <RowBlock
            title="Входы в кабинет"
            rows={of('portal_entry')}
            onAdd={() => addRow('portal_entry')}
            onRemove={(key) => setRows(rows.filter((r) => r.key !== key))}
            columns={['Ссылка', 'Логин', 'Пароль']}
            render={(row) => (
              <>
                <td>
                  <input
                    value={row.url ?? ''}
                    onChange={(e) => patchRow(row.key, { url: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    value={row.login ?? ''}
                    onChange={(e) => patchRow(row.key, { login: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    value={row.password ?? ''}
                    onChange={(e) => patchRow(row.key, { password: e.target.value })}
                    style={{ width: '100%' }}
                  />
                </td>
              </>
            )}
          />
        )}

        {(kind === 'cloud' || kind === 'portal') && (
          <div className="section" style={{ marginTop: 18 }}>
            <h2>Файлы</h2>
            <>
              {current && current.files.length > 0 && (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Файл</th>
                          <th className="num">Размер</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                      {current.files.map((f) => (
                          <tr key={f.id}>
                            <td>{f.original_name}</td>
                            <td className="num mono">{Math.round(f.size / 1024)} КБ</td>
                            <td>
                              <button
                                type="button"
                                className="btn small"
                                onClick={() => api.downloadAccessFile(f.id, f.original_name)}
                              >
                                Скачать
                              </button>{' '}
                              <button
                                type="button"
                                className="btn small danger"
                                onClick={() => removeFile.mutate(f.id)}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              <input
                ref={fileInput}
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) {
                    if (!title.trim()) setError('Сначала укажите название записи')
                    else attach.mutate(f)
                  }
                  e.target.value = ''
                }}
              />
              <button
                type="button"
                className="btn small"
                style={{ marginTop: 10 }}
                disabled={attach.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {attach.isPending ? 'Сохраняем и загружаем…' : '+ Прикрепить файл'}
              </button>
              <span className="hint-inline" style={{ marginLeft: 10 }}>
                Запись сохранится автоматически. Из .rdp подставится адрес сервера.
              </span>
            </>
          </div>
        )}

        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  )
}

interface RowBlockProps {
  title: string
  rows: RowDraft[]
  columns: string[]
  onAdd: () => void
  onRemove: (key: string) => void
  render: (row: RowDraft) => React.ReactNode
}

/** Повторяющийся блок строк с кнопкой «+». */
function RowBlock({ title, rows, columns, onAdd, onRemove, render }: RowBlockProps) {
  return (
    <div className="section" style={{ marginTop: 18 }}>
      <h2>{title}</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c}>{c}</th>
              ))}
              <th style={{ width: 50 }} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="empty">
                  Пусто
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.key}>
                {render(row)}
                <td>
                  <button type="button" className="btn small danger" onClick={() => onRemove(row.key)}>
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" className="btn small" style={{ marginTop: 10 }} onClick={onAdd}>
        + Добавить
      </button>
    </div>
  )
}
